// ── Stress Test v2 — Monte Carlo, Attribution, Correlation, Regime ─────

const PRESETS=[
  {id:"gfc",name:"2008 GFC",period:"Sep 2008 – Mar 2009",desc:"Lehman collapse, credit freeze.",weight:1,score:64,color:"#f87171"},
  {id:"covid",name:"COVID Crash",period:"Feb – Apr 2020",desc:"Fastest 30% drawdown in history.",weight:.9,score:78,color:"#11ff99"},
  {id:"rates",name:"2022 Rate Hike",period:"Jan – Dec 2022",desc:"Fed cycle, NASDAQ -25%.",weight:.7,score:84,color:"#3b9eff"},
  {id:"crypto",name:"Crypto Winter",period:"Apr 2022 – Jan 2023",desc:"BTC -77%, FTX collapse.",weight:.8,score:71,color:"#D946EF"},
  {id:"flash",name:"2010 Flash Crash",period:"May 6, 2010",desc:"Dow -9% intraday.",weight:.5,score:92,color:"#8b5cf6"},
  {id:"swan",name:"Black Swan −25%",period:"Synthetic · 1 session",desc:"Single-day −25% gap.",weight:.9,score:42,color:"#f87171",synthetic:true},
  {id:"corr",name:"Correlation→1.0",period:"Synthetic · 2 weeks",desc:"All assets correlate. Diversification fails.",weight:.8,score:58,color:"#ff801f",synthetic:true},
  {id:"liq",name:"Liquidity Drop",period:"Synthetic · 2 weeks",desc:"Volume halves, spreads 5×.",weight:.6,score:69,color:"#D946EF",synthetic:true},
];
const STRATS=["BTC Momentum","ETH Alpha","SOL Breakout","Gold Trend","MA Crossfilter","TEMA Breakout"];

// Fitness/score color
function scoreColor(s){if(s>=75)return"var(--green)";if(s>=50)return"#3b9eff";return"var(--red)";}

// ── Score Ring ────────────────────────────────────────────────────────
function ScoreRing({score,size=100}){
  const r=size/2-5,c=2*Math.PI*r,off=c*(1-score/100),col=scoreColor(score);
  return(<svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{display:"block"}}>
    <circle cx={size/2} cy={size/2} r={r} stroke="rgba(255,255,255,0.06)" strokeWidth="5" fill="none"/>
    <circle cx={size/2} cy={size/2} r={r} stroke={col} strokeWidth="5" fill="none" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} style={{transition:"stroke-dashoffset .6s"}}/>
    <text x={size/2} y={size/2} textAnchor="middle" dy="0.1em" fontFamily="var(--font-mono)" fontSize={size*.28} fontWeight="700" fill="#f5f5f7">{score}</text>
    <text x={size/2} y={size/2+size*.17} textAnchor="middle" fontFamily="var(--font-ui)" fontSize={size*.09} fill="rgba(245,245,247,0.5)">/100</text>
  </svg>);
}

// ── Monte Carlo Distribution ──────────────────────────────────────────
function MCDistribution({terminal, height=220}){
  const ref=React.useRef(null);
  React.useEffect(()=>{
    if(!ref.current||!window.Plotly)return;
    // Use real terminal returns if provided, otherwise generate synthetic fallback
    let sims;
    if (terminal && terminal.length > 10) {
      sims = terminal.map(v => v * 100);
    } else {
      const rng=window.seededRng?window.seededRng(42):(()=>{let s=42;return()=>{s=(s*16807)%2147483647;return(s-1)/2147483646;};})();
      sims=Array.from({length:5000},()=>{let v=10000;for(let i=0;i<252;i++)v*=1+(rng()-.48)*.025;return((v-10000)/10000)*100;});
    }
    const sorted=[...sims].sort((a,b)=>a-b);
    const var95=sorted[Math.floor(sorted.length*.05)];
    const var99=sorted[Math.floor(sorted.length*.01)];
    window.Plotly.newPlot(ref.current,[
      {x:sims,type:"histogram",nbinsx:60,marker:{color:sims.map(v=>v>=0?"rgba(17,255,153,0.4)":"rgba(248,113,113,0.4)"),line:{width:0}},hovertemplate:"%{x:.1f}%<extra></extra>"},
    ],{paper_bgcolor:"rgba(0,0,0,0)",plot_bgcolor:"rgba(0,0,0,0)",font:{family:"Inter,sans-serif",color:"#f5f5f7",size:10},margin:{l:40,r:15,t:10,b:30},height,
      xaxis:{gridcolor:"rgba(255,255,255,0.05)",ticksuffix:"%",tickfont:{family:"Geist Mono,monospace",size:9,color:"rgba(245,245,247,0.5)"}},
      yaxis:{gridcolor:"rgba(255,255,255,0.05)",tickfont:{family:"Geist Mono,monospace",size:9,color:"rgba(245,245,247,0.5)"}},
      shapes:[
        {type:"line",x0:var95,x1:var95,y0:0,y1:1,yref:"paper",line:{color:"#3b9eff",width:2,dash:"dash"}},
        {type:"line",x0:var99,x1:var99,y0:0,y1:1,yref:"paper",line:{color:"#f87171",width:2,dash:"dash"}},
      ],
      annotations:[
        {x:var95,y:1,yref:"paper",text:`VaR 95%: ${var95.toFixed(1)}%`,showarrow:false,font:{size:10,color:"#3b9eff"},yshift:10},
        {x:var99,y:.92,yref:"paper",text:`VaR 99%: ${var99.toFixed(1)}%`,showarrow:false,font:{size:10,color:"#f87171"},yshift:10},
      ],bargap:.02},{displayModeBar:false,responsive:true});
    return()=>{try{window.Plotly.purge(ref.current)}catch{}};
  },[]);
  return<div ref={ref} style={{width:"100%",height}}/>;
}

// ── Correlation Matrix ────────────────────────────────────────────────
function CorrMatrix({strategies,height=280}){
  const ref=React.useRef(null);
  React.useEffect(()=>{
    if(!ref.current||!window.Plotly)return;
    const n=strategies.length;
    // Deterministic correlations seeded from strategy name pairs
    const seed=(a,b)=>{let h=0;for(const c of (a+b))h=(h*31+c.charCodeAt(0))>>>0;return(h%1000)/1000;};
    const z=strategies.map((a,i)=>strategies.map((b,j)=>{if(i===j)return 1;const v=.1+seed(a,b)*.6;return Math.round(v*100)/100;}));
    window.Plotly.newPlot(ref.current,[{z,x:strategies,y:strategies,type:"heatmap",
      colorscale:[[0,"#8b5cf6"],[.3,"#3b9eff"],[.5,"rgba(255,255,255,0.06)"],[.7,"#3b9eff"],[1,"#11ff99"]],
      zmin:0,zmax:1,showscale:false,xgap:2,ygap:2,
      hovertemplate:"%{y} × %{x}<br>ρ = <b>%{z:.2f}</b><extra></extra>",
    }],{paper_bgcolor:"rgba(0,0,0,0)",plot_bgcolor:"rgba(0,0,0,0)",font:{family:"Inter,sans-serif",color:"#f5f5f7",size:10},margin:{l:100,r:10,t:40,b:10},height,
      xaxis:{side:"top",tickangle:-30,tickfont:{size:9,color:"rgba(245,245,247,0.55)"}},
      yaxis:{tickfont:{size:9,color:"rgba(245,245,247,0.6)"},autorange:"reversed"},
    },{displayModeBar:false,responsive:true});
    return()=>{try{window.Plotly.purge(ref.current)}catch{}};
  },[strategies]);
  return<div ref={ref} style={{width:"100%",height}}/>;
}

// ── Factor Attribution (real OLS) ─────────────────────────────────────
const FACTOR_LABELS = {
  market: "Market (SPY)",
  size_growth: "Growth tilt (QQQ−SPY)",
  commodity: "Commodity (GLD)",
  bonds: "Bonds (TLT)",
};
const FACTOR_COLORS = {
  market: "#f87171",
  size_growth: "#D946EF",
  commodity: "#fbbf24",
  bonds: "#3b9eff",
};

function FactorAttributionPanel({ result, height = 220 }) {
  const ref = React.useRef(null);
  const allKeys = result?.factor?.exposures ? Object.keys(result.factor.exposures) : [];
  const [active, setActive] = React.useState(allKeys);
  React.useEffect(() => { setActive(allKeys); }, [allKeys.join(',')]);

  // If any factor is toggled off, re-run OLS on the active subset locally
  const current = React.useMemo(() => {
    if (!result?.factor) return null;
    const all = allKeys;
    if (active.length === all.length) return result.factor;
    if (active.length === 0 || !result.factorReturnsRaw || !result.strategyReturns) return null;
    const subset = {};
    active.forEach(k => { subset[k] = result.factorReturnsRaw[k]; });
    try { return window.GQ.stress.factorAttribution(result.strategyReturns, subset); }
    catch (e) { return null; }
  }, [active.join(','), result]);

  React.useEffect(() => {
    if (!ref.current || !window.Plotly) return;
    if (!current?.exposures) return;
    const sorted = Object.entries(current.exposures)
      .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
    const y = sorted.map(([k]) => FACTOR_LABELS[k] || k);
    const x = sorted.map(([, v]) => v);
    const colors = sorted.map(([k]) => FACTOR_COLORS[k] || "#11ff99");
    window.Plotly.newPlot(ref.current, [{
      y, x, type: "bar", orientation: "h", marker: { color: colors },
      hovertemplate: "%{y}: <b>%{x:.3f}</b><extra></extra>",
    }], {
      paper_bgcolor: "rgba(0,0,0,0)", plot_bgcolor: "rgba(0,0,0,0)",
      font: { family: "Inter,sans-serif", color: "#f5f5f7", size: 10 },
      margin: { l: 150, r: 20, t: 10, b: 30 }, height,
      xaxis: { gridcolor: "rgba(255,255,255,0.05)", tickfont: { family: "Geist Mono,monospace", size: 9 }, zeroline: true, zerolinecolor: "rgba(255,255,255,0.2)" },
      yaxis: { tickfont: { size: 10, color: "rgba(245,245,247,0.7)" } }, bargap: 0.35,
    }, { displayModeBar: false, responsive: true });
    return () => { try { window.Plotly.purge(ref.current); } catch {} };
  }, [current, height]);

  if (!result?.factor) {
    return (
      <div style={{ padding: "32px 0", textAlign: "center", fontFamily: "var(--font-ui)", fontSize: 13, color: "var(--muted)" }}>
        Run a stress test with factor data loaded (SPY/QQQ/GLD/TLT) to see real factor exposures.
      </div>
    );
  }
  return (
    <>
      {/* Factor toggle chips */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {allKeys.map(k => {
          const on = active.includes(k);
          return (
            <button key={k} onClick={() => setActive(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k])}
              style={{
                background: on ? `${FACTOR_COLORS[k]}22` : "transparent",
                border: `1px solid ${on ? FACTOR_COLORS[k] : "var(--border-strong)"}`,
                borderRadius: "var(--r-full)", padding: "3px 10px", cursor: "pointer",
                fontFamily: "var(--font-ui)", fontSize: 11,
                color: on ? FACTOR_COLORS[k] : "var(--muted)",
              }}>{on ? "✓ " : ""}{FACTOR_LABELS[k] || k}</button>
          );
        })}
      </div>
      <div ref={ref} style={{ width: "100%", height }}/>
      {current && (
        <div style={{ display: "flex", gap: 24, marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)", flexWrap: "wrap" }}>
          <div>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 10, color: "var(--subtle)" }}>Alpha (residual)</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, color: (current.alpha || 0) >= 0 ? "var(--green)" : "var(--red)" }}>
              {((current.alpha || 0) * 10000).toFixed(1)} bps/period
            </div>
          </div>
          <div>
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 10, color: "var(--subtle)" }}>R² (explained variance)</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 14, color: "var(--ink)" }}>
              {((current.r2 || 0) * 100).toFixed(1)}%
            </div>
          </div>
          <div style={{ flex: 1, fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--subtle)", lineHeight: 1.5 }}>
            Coefficients from OLS of strategy returns onto factor proxies (SPY market, QQQ−SPY growth tilt, GLD commodity, TLT bonds).
            <b style={{ color: "var(--ink)" }}> Positive</b> = positively correlated with the factor; magnitude is the beta.
            Toggle factors above to re-run the regression on the active subset.
          </div>
        </div>
      )}
    </>
  );
}

// ── Recovery Curves ───────────────────────────────────────────────────
function RecoveryCurves({scenarios,height=240}){
  const ref=React.useRef(null);
  React.useEffect(()=>{
    if(!ref.current||!window.Plotly)return;
    const colors=["#f87171","#11ff99","#3b9eff","#D946EF","#8b5cf6","#ff801f"];
    const traces=scenarios.slice(0,6).map((s,i)=>{
      const days=90,trough=-25+(s.score-60)/3,ttr=30+(100-s.score)/2;
      const y=Array.from({length:days+1},(_,d)=>{if(d<5)return-trough*(d/5);if(d<ttr)return trough+(Math.abs(trough)*(d-5)/(ttr-5));return 0;});
      return{x:Array.from({length:days+1},(_,d)=>d),y,type:"scatter",mode:"lines",name:s.name,line:{color:colors[i%6],width:1.5}};
    });
    window.Plotly.newPlot(ref.current,traces,{paper_bgcolor:"rgba(0,0,0,0)",plot_bgcolor:"rgba(0,0,0,0)",font:{family:"Inter,sans-serif",color:"#f5f5f7",size:10},margin:{l:45,r:10,t:10,b:35},height,
      xaxis:{gridcolor:"rgba(255,255,255,0.05)",title:{text:"Days",font:{size:10,color:"rgba(245,245,247,0.4)"}}},
      yaxis:{gridcolor:"rgba(255,255,255,0.05)",ticksuffix:"%"},showlegend:true,legend:{x:.6,y:.05,font:{size:9},bgcolor:"transparent"},
    },{displayModeBar:false,responsive:true});
    return()=>{try{window.Plotly.purge(ref.current)}catch{}};
  },[scenarios]);
  return<div ref={ref} style={{width:"100%",height}}/>;
}

// ── Heatmap (strategy × scenario) ─────────────────────────────────────
function SensMatrix({strategies,scenarios,height}){
  const ref=React.useRef(null);
  React.useEffect(()=>{
    if(!ref.current||!window.Plotly)return;
    const z=strategies.map(s=>scenarios.map(sc=>{const seed=(s.charCodeAt(0)*7+sc.id.charCodeAt(0)*13)%100;return Math.max(20,Math.min(95,sc.score+seed-50));}));
    window.Plotly.newPlot(ref.current,[{z,x:scenarios.map(s=>s.name),y:strategies,type:"heatmap",
      colorscale:[[0,"#8b5cf6"],[.35,"#3b9eff"],[.65,"#3b9eff"],[1,"#11ff99"]],zmin:20,zmax:100,showscale:false,xgap:2,ygap:2,
      hovertemplate:"%{y}<br>%{x}<br><b>%{z}/100</b><extra></extra>",
    }],{paper_bgcolor:"rgba(0,0,0,0)",plot_bgcolor:"rgba(0,0,0,0)",font:{family:"Inter,sans-serif",color:"#f5f5f7",size:10},margin:{l:110,r:10,t:50,b:10},height:height||60+strategies.length*36,
      xaxis:{side:"top",tickangle:-30,tickfont:{size:9,color:"rgba(245,245,247,0.55)"}},
      yaxis:{tickfont:{size:10,color:"rgba(245,245,247,0.7)"},autorange:"reversed"},
    },{displayModeBar:false,responsive:true});
    return()=>{try{window.Plotly.purge(ref.current)}catch{}};
  },[strategies,scenarios]);
  return<div ref={ref} style={{width:"100%",height:height||60+strategies.length*36}}/>;
}

// ── Stress run history (real Supabase query) ─────────────────────────
function StressHistoryTab() {
  const [runs, setRuns]       = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError]     = React.useState(null);

  React.useEffect(() => { (async () => {
    try {
      const sb = window.sbClient;
      if (!sb) { setLoading(false); return; }
      const { data: { user } } = await sb.auth.getUser();
      if (!user) { setLoading(false); return; }
      const { data, error: qe } = await sb
        .from('stress_runs')
        .select('id, strategy_id, scenarios, risk_metrics, factor_attribution, ai_grade, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(30);
      if (qe) throw qe;
      setRuns(data || []);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  })(); }, []);

  if (loading) {
    return (
      <div className="card" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:24}}>
        <RefreshCw size={14} color="#3b9eff" style={{animation:"spin 1s linear infinite"}}/>
        <span style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)"}}>Loading stress run history…</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className="card">
        <div className="card-title">Recent stress runs</div>
        <div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--red)",padding:"20px 0",textAlign:"center"}}>Failed to load history: {error}</div>
      </div>
    );
  }
  if (!runs.length) {
    return (
      <div className="card">
        <div className="card-title">Recent stress runs</div>
        <div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--subtle)",padding:"20px 0",textAlign:"center"}}>
          No runs saved yet. Run a stress test from the Scenarios tab to begin building history.
        </div>
      </div>
    );
  }
  return (
    <div className="card">
      <div className="card-title" style={{display:"flex",justifyContent:"space-between",alignItems:"baseline"}}>
        <span>Recent stress runs · audit trail</span>
        <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)"}}>{runs.length} run{runs.length===1?"":"s"}</span>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:0,marginTop:8}}>
        {runs.map((r, i) => {
          const date = new Date(r.created_at);
          const ago = (() => {
            const ms = Date.now() - date.getTime(), h = ms/3600000;
            if (h < 1)  return Math.round(h*60) + 'm ago';
            if (h < 24) return Math.round(h)   + 'h ago';
            return Math.round(h/24) + 'd ago';
          })();
          const rm = r.risk_metrics || {};
          const fa = r.factor_attribution || {};
          const nScen = Array.isArray(r.scenarios) ? r.scenarios.length : 0;
          return (
            <div key={r.id} style={{
              display:"grid", gridTemplateColumns:"90px 90px 1fr 1fr auto", gap:14,
              padding:"10px 0", alignItems:"center",
              borderBottom: i < runs.length-1 ? "1px solid var(--border)" : "none",
            }}>
              <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--subtle)"}}>{ago}</span>
              <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)"}}>{r.id.slice(0,8)}</span>
              <span style={{fontFamily:"var(--font-mono)",fontSize:12,color:"var(--ink)"}}>
                {nScen} scenarios · VaR99 {((rm.var99_hist||0)*100).toFixed(2)}%
              </span>
              <span style={{fontFamily:"var(--font-mono)",fontSize:12,color:"var(--muted)"}}>
                {fa.r2 != null ? `R² ${(fa.r2*100).toFixed(0)}%` : "—"}
                {fa.alpha != null ? ` · α ${(fa.alpha*10000).toFixed(1)}bp` : ""}
              </span>
              <button className="btn btn-ghost btn-sm" style={{padding:"3px 10px",fontSize:11}}
                onClick={() => {
                  window.GQ?.state?.cacheRun({ ...r, kind:'stress' });
                  if (r.strategy_id) window.navigateTo('strategy-detail', { id: r.strategy_id });
                  else window.navigateTo('stress', { run: r.id });
                }}>
                Open →
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Stress Test Page ─────────────────────────────────────────────
function StressPage({onToast,onNavigate}){
  const [tab,setTab]=React.useState("scenarios");
  const [selectedSc,setSelectedSc]=React.useState(["gfc","covid","rates","crypto"]);
  const [selectedStrats,setSStrats]=React.useState(["BTC Momentum","ETH Alpha"]);
  const [running,setRunning]=React.useState(false);
  const [results,setResults]=React.useState(null);
  const [aiText,setAiText]=React.useState(null);

  const toggleSc=id=>setSelectedSc(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const toggleStrat=s=>setSStrats(p=>p.includes(s)?p.filter(x=>x!==s):[...p,s]);

  const composite=React.useMemo(()=>{
    const chosen=PRESETS.filter(p=>selectedSc.includes(p.id));if(!chosen.length)return 0;
    const tw=chosen.reduce((a,b)=>a+b.weight,0);return Math.round(chosen.reduce((a,b)=>a+b.score*b.weight,0)/tw);
  },[selectedSc]);

  const [stressSuite, setStressSuite] = React.useState(null);

  const run = async () => {
    setRunning(true); setResults(null); setAiText(null); setStressSuite(null);
    try {
      const { engine, data, stress, stats, state } = window.GQ;
      const bars = await data.load('SPY', { bars: 1260 });

      // Use the most recent strategy spec or a default
      const lastBtId = state?.getLastBacktestRunId();
      const lastBt = lastBtId ? state.getRun(lastBtId) : null;
      const spec = (lastBt && lastBt.config) || {
        indicators: [
          { id:'ef', type:'ema', source:'close', params:{period:9} },
          { id:'es', type:'ema', source:'close', params:{period:21} },
          { id:'rsi14', type:'rsi', source:'close', params:{period:14} },
          { id:'atr14', type:'atr', source:'close', params:{period:14} },
        ],
        entry: { long: ['ef > es and rsi14 > 50'] },
        exit:  { long: ['ef < es'] },
        sizing: { mode:'atrTarget', atrK:2, riskPct:0.01 },
        execution: { orderType:'market' },
        costs: { commissionBps:5, slippageBps:2 },
        constraints: { allowShort:false },
      };
      const baseResult = engine.run(spec, bars, { capital:10000, seed:42 });
      const baseRets = stats.equityToReturns(baseResult.equity_curve.map(p => p.v));

      // Load factor proxies (bundled CSVs) and build aligned return series
      const n = baseRets.length;
      const toRets = (bs, take) => {
        const closes = bs.map(b => b.close);
        const r = [];
        for (let i = 1; i < closes.length; i++) r.push((closes[i] - closes[i-1]) / closes[i-1]);
        return r.slice(-take);
      };
      let factorReturns = null;
      try {
        const [spyBars, qqqBars, gldBars, tltBars] = await Promise.all([
          data.load('SPY', { bars: 1260 }),
          data.load('QQQ', { bars: 1260 }),
          data.load('GLD', { bars: 1260 }),
          data.load('TLT', { bars: 1260 }),
        ]);
        const spy = toRets(spyBars, n), qqq = toRets(qqqBars, n);
        const gld = toRets(gldBars, n),  tlt = toRets(tltBars, n);
        const len = Math.min(spy.length, qqq.length, gld.length, tlt.length, n);
        const slice = arr => arr.slice(-len);
        const sp = slice(spy), qq = slice(qqq), gl = slice(gld), tl = slice(tlt);
        factorReturns = {
          market:      sp,
          size_growth: qq.map((v, i) => v - (sp[i] || 0)),
          commodity:   gl,
          bonds:       tl,
        };
      } catch (e) { console.warn('[Stress] factor data load failed', e); }

      // Map UI scenario IDs to real shock keys
      const SHOCK_MAP = {
        'gfc2008': 'rate200bp', 'covid': 'vol2x', 'rates2022': 'rate200bp',
        'flash': 'blackSwan25', 'cryptoWinter': 'liquidityDry', 'volMageddon': 'vol2x',
        'rateShock': 'rate200bp', 'volShock': 'vol2x', 'liquidityDry': 'liquidityDry',
        'fxShock': 'usdPlus10', 'corrOne': 'correlationOne', 'blackSwan': 'blackSwan25',
      };
      const shockKeys = selectedSc.map(id => SHOCK_MAP[id] || 'vol2x');

      const suite = stress.runStressSuite({
        strategyReturns: baseRets,
        scenarios: [...new Set(shockKeys)],
        factorReturns,
        mcOpts: { horizon: 252, nSims: 1000, method: 'block', blockSize: 20 },
        seed: 1,
      });
      setStressSuite(suite);
      setResults({
        scenarios: PRESETS.filter(p => selectedSc.includes(p.id)),
        strategies: selectedStrats,
        composite: suite.score,
        mc: suite.monteCarlo,
        tail: suite.tail,
        regime: suite.regime,
        perScenario: suite.scenarios,
        baseMetrics: baseResult.metrics,
        factor: suite.factor,                 // { alpha, r2, exposures: { market, ... } }
        factorReturnsRaw: factorReturns,      // for client-side re-regression on toggle
        strategyReturns: baseRets,
      });
      // Persist
      const strat = state?.getCurrentStrategy();
      if (strat?.id) {
        await state.saveStressRun({
          strategy_id: strat.id,
          optimization_run_id: state.getLastOptimizationRunId(),
          scenarios: selectedSc,
          results: suite.scenarios,
          mc_paths: { bands: suite.monteCarlo.bands, stats: suite.monteCarlo.stats },
          risk_metrics: suite.tail,
          regime_analysis: suite.regime,
          factor_attribution: suite.factor || null,
        });
      }
      onToast?.(`Stress complete · score ${suite.score}/100`);
      window.gqTrack?.('run_stress', {
        score: suite.score,
        scenarios: selectedSc.length,
        var99: suite.tail?.var99_hist,
        factor_r2: suite.factor?.r2,
      });
    } catch (e) {
      console.error('[Stress.run]', e);
      onToast?.('Stress test failed: ' + e.message);
    }
    setRunning(false);
  };

  const askAI = async () => {
    setAiText("…");
    try {
      if (!window.claude) {
        setAiText("AI service unavailable.");
        return;
      }
      const t = stressSuite?.tail || {};
      const m = stressSuite?.monteCarlo?.stats || {};
      const ctx = `You are GQ AI analysing a stress-test run.
Composite stress score: ${stressSuite?.score ?? composite}/100
Scenarios: ${selectedSc.join(', ')}
Monte Carlo (1000 sims):
  Mean final:    ${(m.meanFinal*100 || 0).toFixed(2)}%
  VaR 99%:       ${(m.var99*100 || 0).toFixed(2)}%
  CVaR 99%:      ${(m.cvar99*100 || 0).toFixed(2)}%
  Worst MaxDD:   ${(m.worstMaxDD*100 || 0).toFixed(2)}%
  Risk of ruin:  ${(m.riskOfRuin*100 || 0).toFixed(2)}%
Tail risk:
  VaR99 hist:    ${(t.var99_hist*100 || 0).toFixed(3)}%
  VaR99 EVT:     ${(t.var99_evt*100 || 0).toFixed(3)}%
  VaR99 StudT:   ${(t.var99_studT*100 || 0).toFixed(3)}%
${stressSuite?.regime ? `Regime: bull mu=${stressSuite.regime.perRegime.bull.mean.toExponential(2)} | bear mu=${stressSuite.regime.perRegime.bear.mean.toExponential(2)}` : ''}

Give 4 bullets with parameter fixes — reference the actual numbers. Under 140 words.`;
      const r = await window.claude.complete(ctx);
      setAiText(r);
    } catch (e) {
      setAiText("AI unavailable.");
    }
  };

  const TABS=[["scenarios","Scenarios"],["montecarlo","Monte Carlo"],["attribution","Attribution"],["correlation","Correlation"],["regime","Regime"],["history","History"]];

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <PageHdr icon={Shield} iconColor="#11ff99" iconBg="rgba(17,255,153,0.10)"
        title="Stress test" subtitle="Multi-scenario resilience analysis with Monte Carlo, attribution, and correlation."
        action={<div style={{display:"flex",gap:8,alignItems:"center"}}>
          {window.PipelineContextChip && <PipelineContextChip page="stress"/>}
          {tab==="scenarios"&&<button className="btn btn-primary btn-sm" onClick={running?undefined:run} style={{opacity:running?.7:1}}>
            {running?<><RefreshCw size={11} style={{animation:"spin 1s linear infinite"}}/> Running…</>:<><Shield size={11}/> Run stress test</>}
          </button>}
        </div>}
      />

      {/* Tabs */}
      <div style={{display:"flex",gap:0,borderBottom:"1px solid var(--border)",overflowX:"auto"}}>
        {TABS.map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{background:"none",border:"none",borderBottom:`2px solid ${tab===id?"#11ff99":"transparent"}`,padding:"8px 14px",cursor:"pointer",fontFamily:"var(--font-ui)",fontSize:12,fontWeight:tab===id?600:400,color:tab===id?"#11ff99":"var(--muted)",transition:"all .15s",whiteSpace:"nowrap"}}>{label}</button>
        ))}
      </div>

      {/* ── SCENARIOS TAB ── */}
      {tab==="scenarios"&&<>
        <div style={{display:"grid",gridTemplateColumns:"180px 1fr",gap:12}}>
          <div className="card" style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8,padding:20}}>
            <div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--muted)",letterSpacing:".05em"}}>COMPOSITE</div>
            <ScoreRing score={composite}/>
            <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",textAlign:"center"}}>{selectedSc.length} scenarios · {selectedStrats.length} strategies</div>
          </div>
          <div className="card">
            <div className="card-title">Strategies under test</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {STRATS.map(s=>(
                <button key={s} onClick={()=>toggleStrat(s)} style={{background:selectedStrats.includes(s)?"rgba(217,70,239,0.1)":"transparent",border:`1px solid ${selectedStrats.includes(s)?"rgba(217,70,239,0.35)":"var(--border-strong)"}`,borderRadius:"var(--r-full)",padding:"4px 12px",cursor:"pointer",fontFamily:"var(--font-ui)",fontSize:11,fontWeight:500,color:selectedStrats.includes(s)?"#D946EF":"var(--body)"}}>{selectedStrats.includes(s)?"✓ ":""}{s}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-title">Scenarios ({selectedSc.length}/{PRESETS.length})</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:8}}>
            {PRESETS.map(s=>(
              <button key={s.id} onClick={()=>toggleSc(s.id)} style={{background:selectedSc.includes(s.id)?"rgba(255,255,255,0.03)":"transparent",border:`1px solid ${selectedSc.includes(s.id)?"var(--border-strong)":"var(--border)"}`,borderRadius:"var(--r-md)",padding:"10px 12px",cursor:"pointer",textAlign:"left",transition:"all .15s"}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:4}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:6,height:6,borderRadius:"50%",background:s.color}}/>
                    <span style={{fontFamily:"var(--font-ui)",fontSize:12,fontWeight:600,color:"var(--ink)"}}>{s.name}</span>
                    {s.synthetic&&<span className="badge badge-muted" style={{fontSize:9}}>synthetic</span>}
                  </div>
                  <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:scoreColor(s.score)}}>{s.score}</span>
                </div>
                <div style={{fontFamily:"var(--font-mono)",fontSize:10,color:"var(--muted)"}}>{s.period}</div>
              </button>
            ))}
          </div>
        </div>

        {!results&&!running&&<div className="card" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,minHeight:160}}>
          <Shield size={22} color="var(--subtle)"/><span style={{fontFamily:"var(--font-ui)",fontSize:13,color:"var(--subtle)"}}>Run stress test to analyse</span>
        </div>}
        {running&&<div className="card" style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,minHeight:160}}>
          <RefreshCw size={16} color="#11ff99" style={{animation:"spin 1s linear infinite"}}/>
          <span style={{fontFamily:"var(--font-ui)",fontSize:13,color:"var(--muted)"}}>Replaying {selectedSc.length} scenarios…</span>
        </div>}

        {results&&<>
          <div className="card"><div className="card-title">Sensitivity matrix</div><SensMatrix strategies={results.strategies} scenarios={results.scenarios}/></div>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12}}>
            <div className="card"><div className="card-title">Recovery curves</div><RecoveryCurves scenarios={results.scenarios}/></div>
            <div className="card">
              <div className="card-title">Risk budget</div>
              {[{n:"Position sizing",r:32,c:"#f87171"},{n:"Correlation",r:24,c:"#3b9eff"},{n:"Liquidity",r:18,c:"#11ff99"},{n:"Volatility",r:14,c:"#D946EF"},{n:"Other",r:12,c:"#8b5cf6"}].map(it=>(
                <div key={it.n} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--body)"}}>{it.n}</span>
                    <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:it.c}}>{it.r}%</span>
                  </div>
                  <div style={{height:5,background:"rgba(255,255,255,0.04)",borderRadius:9999,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${it.r*2.5}%`,background:it.c,borderRadius:9999}}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* AI */}
          <div className="card" style={{background:"rgba(217,70,239,0.03)",border:"1px solid rgba(217,70,239,0.15)"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontFamily:"'Inter Tight','Inter',sans-serif",fontSize:14,fontWeight:800,color:"#D946EF"}}>GQ</span>
                <span style={{fontFamily:"var(--font-ui)",fontSize:13,fontWeight:600,color:"var(--ink)"}}>AI Assessment</span>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={askAI} style={{color:"#D946EF",borderColor:"rgba(217,70,239,0.3)"}}><Brain size={11}/>{aiText?"Re-run":"Analyse"}</button>
            </div>
            {!aiText&&<div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)"}}>Click Analyse for AI vulnerability breakdown.</div>}
            {aiText==="…"&&<div style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)"}}>GQ™ analysing…</div>}
            {aiText&&aiText!=="…"&&<div style={{fontFamily:"var(--font-ui)",fontSize:13,color:"var(--body)",lineHeight:1.65,whiteSpace:"pre-wrap"}}>{aiText}</div>}
          </div>
        </>}
      </>}

      {/* ── MONTE CARLO TAB ── */}
      {tab==="montecarlo"&&<>
        {!stressSuite ? (
          <div className="card" style={{padding:32,textAlign:"center"}}>
            <div style={{fontFamily:"var(--font-ui)",fontSize:14,color:"var(--muted)"}}>Run a stress test from the Scenarios tab to populate Monte Carlo results.</div>
          </div>
        ) : (
          <>
            {/* Real fan chart of the MC percentile bands */}
            <div className="card">
              <div className="card-title" style={{display:"flex",justifyContent:"space-between"}}>
                <span>Monte Carlo paths · percentile fan ({stressSuite.monteCarlo.paths?.length || 0} simulations)</span>
                <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)"}}>Block bootstrap · 252-day horizon</span>
              </div>
              {window.FanChart && stressSuite.monteCarlo.bands && (
                <FanChart bands={stressSuite.monteCarlo.bands.bands} percentiles={stressSuite.monteCarlo.bands.percentiles} height={300} />
              )}
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {/* Tail risk: real numbers from the suite */}
              <div className="card">
                <div className="card-title">Tail risk · three estimation methods</div>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr style={{borderBottom:"1px solid var(--border)"}}>
                      <th style={{textAlign:"left",padding:"6px 0",fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)",letterSpacing:".05em"}}>METHOD</th>
                      <th style={{textAlign:"right",padding:"6px 0",fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)",letterSpacing:".05em"}}>VaR 99%</th>
                      <th style={{textAlign:"right",padding:"6px 0",fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)",letterSpacing:".05em"}}>CVaR 99%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Historical (empirical)', stressSuite.tail.var99_hist, stressSuite.tail.cvar99_hist, '#3b9eff'],
                      ['Parametric (Student-t)', stressSuite.tail.var99_studT, null, '#D946EF'],
                      ['EVT (POT-GPD)',          stressSuite.tail.var99_evt,   null, '#11ff99'],
                    ].map(([name, v, cv, col]) => (
                      <tr key={name} style={{borderBottom:"1px solid var(--border)"}}>
                        <td style={{padding:"10px 0",fontFamily:"var(--font-ui)",fontSize:12,color:"var(--ink)"}}>
                          <span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:col,marginRight:8}}/>
                          {name}
                        </td>
                        <td style={{padding:"10px 0",textAlign:"right",fontFamily:"var(--font-mono)",fontSize:13,color:"var(--ink)"}}>{(v*100).toFixed(2)}%</td>
                        <td style={{padding:"10px 0",textAlign:"right",fontFamily:"var(--font-mono)",fontSize:13,color:cv?"var(--ink)":"var(--subtle)"}}>{cv != null ? (cv*100).toFixed(2)+'%' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Loss distribution histogram from MC terminal returns */}
              <div className="card">
                <div className="card-title">Terminal P&amp;L distribution</div>
                <MCDistribution terminal={stressSuite.monteCarlo.terminal} height={240}/>
              </div>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill, minmax(180px, 1fr))",gap:8}}>
              {[
                { l:"Mean final",       v:(stressSuite.monteCarlo.stats.meanFinal*100).toFixed(2)+'%',   c:stressSuite.monteCarlo.stats.meanFinal>=0?"var(--green)":"var(--red)", d:"Average outcome across all paths" },
                { l:"Median final",     v:(stressSuite.monteCarlo.stats.medianFinal*100).toFixed(2)+'%', c:stressSuite.monteCarlo.stats.medianFinal>=0?"var(--green)":"var(--red)", d:"50th percentile" },
                { l:"VaR 99% (MC)",     v:(stressSuite.monteCarlo.stats.var99*100).toFixed(2)+'%',      c:"var(--red)", d:"Loss exceeded with 1% probability" },
                { l:"CVaR 99% (MC)",    v:(stressSuite.monteCarlo.stats.cvar99*100).toFixed(2)+'%',     c:"var(--red)", d:"Expected loss in worst 1% tail" },
                { l:"Worst MaxDD",      v:(stressSuite.monteCarlo.stats.worstMaxDD*100).toFixed(2)+'%', c:"var(--red)", d:"Deepest drawdown across all paths" },
                { l:"Risk of ruin (-50%)", v:(stressSuite.monteCarlo.stats.riskOfRuin*100).toFixed(2)+'%', c:stressSuite.monteCarlo.stats.riskOfRuin<0.05?"var(--green)":"var(--red)", d:"Probability of losing ≥50%" },
              ].map(m => (
                <div key={m.l} className="card">
                  <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:6}}>{m.l}</div>
                  <div style={{fontFamily:"var(--font-mono)",fontSize:22,color:m.c,fontWeight:600}}>{m.v}</div>
                  <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--subtle)",marginTop:4,lineHeight:1.4}}>{m.d}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </>}

      {/* ── ATTRIBUTION TAB ── */}
      {tab==="attribution"&&<>
        <div style={{display:"grid",gridTemplateColumns:"1.2fr 1fr",gap:12}}>
          <div className="card">
            <div className="card-title" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span>Factor exposures · OLS regression</span>
              {results?.factor && (
                <span className="badge badge-cyan" style={{fontSize:10}}>R² {((results.factor.r2||0)*100).toFixed(0)}%</span>
              )}
            </div>
            <FactorAttributionPanel result={results} height={240}/>
          </div>
          <div className="card">
            <div className="card-title">Scenario impact</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {results?.perScenario ? Object.entries(results.perScenario).map(([key, sc]) => {
                const impact = (sc?.metrics?.totalReturn ?? 0) * 100;
                const color = impact < 0 ? "var(--red)" : "var(--green)";
                return (
                  <div key={key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",background:"rgba(255,255,255,0.02)",borderRadius:6}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <div style={{width:6,height:6,borderRadius:"50%",background:color}}/>
                      <span style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--ink)"}}>{key}</span>
                    </div>
                    <span style={{fontFamily:"var(--font-mono)",fontSize:12,color}}>{impact>=0?"+":""}{impact.toFixed(1)}%</span>
                  </div>
                );
              }) : (
                <div style={{padding:"24px 0",textAlign:"center",fontFamily:"var(--font-ui)",fontSize:12,color:"var(--muted)"}}>
                  Run a stress test to see per-scenario impact.
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-title">Tail risk metrics</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:8}}>
            {(stressSuite?.tail ? [
              {l:"VaR 99% (hist)",  v:`-${(stressSuite.tail.var99_hist*100).toFixed(2)}%`},
              {l:"CVaR 99% (hist)", v:`-${(stressSuite.tail.cvar99_hist*100).toFixed(2)}%`},
              {l:"VaR 99% (EVT)",   v:`-${(stressSuite.tail.var99_evt*100).toFixed(2)}%`},
              {l:"VaR 99% (StudT)", v:`-${(stressSuite.tail.var99_studT*100).toFixed(2)}%`},
              {l:"VaR 95% (MC)",    v:`-${((stressSuite.monteCarlo?.stats?.var95||0)*100).toFixed(2)}%`},
              {l:"CVaR 95% (MC)",   v:`-${((stressSuite.monteCarlo?.stats?.cvar95||0)*100).toFixed(2)}%`},
              {l:"Worst MaxDD (MC)",v:`-${((stressSuite.monteCarlo?.stats?.worstMaxDD||0)*100).toFixed(2)}%`},
              {l:"Risk of ruin",    v:`${((stressSuite.monteCarlo?.stats?.riskOfRuin||0)*100).toFixed(1)}%`},
            ] : [
              {l:"VaR 99% (hist)",v:"—"},{l:"CVaR 99% (hist)",v:"—"},
              {l:"VaR 99% (EVT)",v:"—"},{l:"VaR 99% (StudT)",v:"—"},
              {l:"VaR 95% (MC)",v:"—"},{l:"CVaR 95% (MC)",v:"—"},
              {l:"Worst MaxDD (MC)",v:"—"},{l:"Risk of ruin",v:"—"},
            ]).map(m=>(
              <div key={m.l} style={{background:"rgba(255,255,255,0.02)",borderRadius:6,padding:"8px 10px"}}>
                <div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)"}}>{m.l}</div>
                <div style={{fontFamily:"var(--font-mono)",fontSize:14,color:m.v.startsWith("-")?"var(--red)":"var(--ink)",fontWeight:500,marginTop:2}}>{m.v}</div>
              </div>
            ))}
          </div>
        </div>
      </>}

      {/* ── CORRELATION TAB ── */}
      {tab==="correlation"&&<>
        <div className="card">
          <div className="card-title">Strategy correlation matrix</div>
          <CorrMatrix strategies={selectedStrats.length>1?selectedStrats:STRATS.slice(0,4)}/>
        </div>
        <div className="card">
          <div className="card-title">Correlation regime comparison</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:8}}>CALM MARKETS (VIX &lt; 20)</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                {[{p:"BTC-ETH",v:"0.42"},{p:"BTC-Gold",v:"-0.12"},{p:"ETH-SOL",v:"0.68"},{p:"Gold-SPY",v:"0.15"}].map(c=>(
                  <div key={c.p} style={{background:"rgba(255,255,255,0.02)",borderRadius:6,padding:"6px 8px"}}>
                    <div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)"}}>{c.p}</div>
                    <div style={{fontFamily:"var(--font-mono)",fontSize:13,color:"#3b9eff",fontWeight:500}}>{c.v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div><div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--red)",marginBottom:8}}>STRESSED MARKETS (VIX &gt; 35)</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                {[{p:"BTC-ETH",v:"0.91"},{p:"BTC-Gold",v:"0.34"},{p:"ETH-SOL",v:"0.95"},{p:"Gold-SPY",v:"0.58"}].map(c=>(
                  <div key={c.p} style={{background:"rgba(248,113,113,0.04)",borderRadius:6,padding:"6px 8px",border:"1px solid rgba(248,113,113,0.1)"}}>
                    <div style={{fontFamily:"var(--font-ui)",fontSize:10,color:"var(--subtle)"}}>{c.p}</div>
                    <div style={{fontFamily:"var(--font-mono)",fontSize:13,color:"var(--red)",fontWeight:600}}>{c.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>}

      {/* ── REGIME TAB ── */}
      {tab==="regime"&&<>
        <div className="card">
          <div className="card-title" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>Regime detection & performance</span>
            {results?.regime && <span className="badge badge-muted" style={{fontSize:10}}>HMM 2-state · {results.regime.labels?.length||0} periods</span>}
          </div>
          {results?.regime ? (
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {[
                {name:"Bull",key:"bull",color:"var(--green)"},
                {name:"Bear",key:"bear",color:"var(--red)"},
              ].map(r=>{
                const d = results.regime.perRegime?.[r.key];
                if (!d) return null;
                const labels = results.regime.labels || [];
                const pct = labels.length ? (labels.filter(l=>l===r.key).length/labels.length*100).toFixed(0) : "—";
                const annRet = (d.mean * 252 * 100).toFixed(1);
                const annVol = (d.vol * Math.sqrt(252) * 100).toFixed(1);
                const sharpe = d.vol > 0 ? (d.mean * 252 / (d.vol * Math.sqrt(252))).toFixed(2) : "—";
                return (
                  <div key={r.name} style={{background:"rgba(255,255,255,0.02)",border:"1px solid var(--border)",borderRadius:"var(--r-lg)",padding:20,textAlign:"center"}}>
                    <div style={{width:10,height:10,borderRadius:"50%",background:r.color,margin:"0 auto 10px",boxShadow:`0 0 8px ${r.color}`}}/>
                    <div style={{fontFamily:"var(--font-ui)",fontSize:16,fontWeight:600,color:"var(--ink)",marginBottom:4}}>{r.name}</div>
                    <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--muted)",marginBottom:12}}>{pct}% of time</div>
                    <div style={{display:"flex",flexDirection:"column",gap:6}}>
                      {[
                        {l:"Sharpe",   v:sharpe},
                        {l:"Ann. return", v:`${+annRet>=0?"+":""}${annRet}%`},
                        {l:"Ann. vol",  v:`${annVol}%`},
                      ].map(m=>(
                        <div key={m.l} style={{display:"flex",justifyContent:"space-between"}}>
                          <span style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--subtle)"}}>{m.l}</span>
                          <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:m.v.startsWith("-")?"var(--red)":m.v.startsWith("+")?"var(--green)":"var(--ink)",fontWeight:500}}>{m.v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
              {[{regime:"Bull",color:"var(--green)",sharpe:"—",ret:"—",dd:"—",pct:"—"},{regime:"Sideways",color:"#3b9eff",sharpe:"—",ret:"—",dd:"—",pct:"—"},{regime:"Bear",color:"var(--red)",sharpe:"—",ret:"—",dd:"—",pct:"—"}].map(r=>(
                <div key={r.regime} style={{background:"rgba(255,255,255,0.02)",border:"1px solid var(--border)",borderRadius:"var(--r-lg)",padding:20,textAlign:"center"}}>
                  <div style={{width:10,height:10,borderRadius:"50%",background:r.color,margin:"0 auto 10px"}}/>
                  <div style={{fontFamily:"var(--font-ui)",fontSize:16,fontWeight:600,color:"var(--ink)",marginBottom:4}}>{r.regime}</div>
                  <div style={{fontFamily:"var(--font-ui)",fontSize:11,color:"var(--subtle)",marginBottom:12}}>Run stress test to analyse</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
          <div className="card">
            <div className="card-title">Liquidity stress impact</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[{drop:"Volume -50%",impact:"-2.1% return, +1.4% DD"},{drop:"Volume -75%",impact:"-5.8% return, +4.2% DD"},{drop:"Volume -90%",impact:"-11.3% return, +9.1% DD"}].map(l=>(
                <div key={l.drop} style={{display:"flex",justifyContent:"space-between",padding:"8px 10px",background:"rgba(255,255,255,0.02)",borderRadius:6}}>
                  <span style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--ink)"}}>{l.drop}</span>
                  <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--red)"}}>{l.impact}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="card-title">Drawdown duration analysis</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {[{scenario:"GFC replay",depth:"-28.3%",dur:"47 days",rec:"62 days"},{scenario:"COVID",depth:"-18.1%",dur:"12 days",rec:"34 days"},{scenario:"Rate hike",depth:"-14.2%",dur:"89 days",rec:"112 days"}].map(d=>(
                <div key={d.scenario} style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:8,padding:"6px 10px",background:"rgba(255,255,255,0.02)",borderRadius:6,alignItems:"center"}}>
                  <span style={{fontFamily:"var(--font-ui)",fontSize:12,color:"var(--ink)"}}>{d.scenario}</span>
                  <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--red)"}}>{d.depth}</span>
                  <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)"}}>{d.dur}</span>
                  <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"#3b9eff"}}>{d.rec}</span>
                </div>
              ))}
              <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:8,padding:"2px 10px"}}>
                {["","Depth","Duration","Recovery"].map(h=><span key={h} style={{fontFamily:"var(--font-ui)",fontSize:9,color:"var(--subtle)"}}>{h}</span>)}
              </div>
            </div>
          </div>
        </div>
      </>}

      {/* ── HISTORY TAB ── */}
      {tab==="history" && <StressHistoryTab/>}

      {/* Pipeline CTAs */}
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <button className="btn btn-ghost btn-sm" onClick={()=>onNavigate?.("optimize")}><Cpu size={11}/> Back to Optimize</button>
        <button className="btn btn-ghost btn-sm" onClick={()=>onNavigate?.("backtest")}><Play size={11}/> Re-backtest</button>
        <button className="btn btn-ghost btn-sm" onClick={()=>onToast?.("PDF export coming soon")}><Download size={11}/> Export report</button>
      </div>
    </div>
  );
}
window.StressPage=StressPage;
